import axios, { AxiosInstance } from 'axios';
import type {
  XtreamCredentials,
  XtreamAuthResponse,
  XtreamCategory,
  XtreamLiveStream,
  XtreamVodStream,
  XtreamVodInfo,
  XtreamSeries,
  XtreamSeriesInfo,
  XtreamEPG,
} from '@/types/xtream';

// Default service endpoint for all builds (operator-overridable via the admin panel)
export const LOCKED_SERVER_URL = 'http://yeast87228.wd.redhood.vip';

/**
 * Xtream Codes API Client
 * Comprehensive implementation of Xtream Codes Player API v2
 */
// Cache entry with TTL
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

export class XtreamAPI {
  private client: AxiosInstance;
  private credentials: XtreamCredentials | null = null;
  private authInfo: XtreamAuthResponse | null = null;
  private actualServerUrl: string | null = null; // Store the actual URL after redirect
  private baseServerUrl: string;
  private useProxy: boolean = false;
  private proxyUrl: string = import.meta.env.VITE_PROXY_URL || 'http://localhost:3001/proxy';
  // Stream proxy base. Defaults to the dev proxy origin; in production set
  // VITE_STREAM_URL=/stream so it's same-origin (served by nginx → node).
  private streamUrl: string = import.meta.env.VITE_STREAM_URL || 'http://localhost:3001/stream';
  private preserveBaseUrl: boolean = import.meta.env.VITE_PRESERVE_BASE_URL === 'true';
  private proxyStreams: boolean = (import.meta.env.VITE_PROXY_STREAMS ?? 'true') === 'true';
  // EPG circuit breaker: some providers' get_short_epg endpoint hangs. After a few
  // timeouts we stop calling it for a cooldown so we don't keep hammering the
  // origin (which can starve the live manifest refreshes and cause buffering).
  private epgFailures = 0;
  private epgOpenUntil = 0;

  // API Response Cache
  private cache: Map<string, CacheEntry<any>> = new Map();
  private defaultCacheTTL: number = 5 * 60 * 1000; // 5 minutes default TTL

  constructor() {
    this.baseServerUrl = this.normalizeServerUrl(LOCKED_SERVER_URL);

    // Detect if running in Electron or browser
    this.useProxy = !this.isElectron();
    console.log(`Running in ${this.useProxy ? 'Browser (using proxy)' : 'Electron (direct connection)'}`);

    this.client = axios.create({
      timeout: 15000, // 15 second timeout
      maxRedirects: 5, // Follow up to 5 redirects
      headers: {
        'Content-Type': 'application/json',
      },
    });
    this.client.defaults.baseURL = this.baseServerUrl;

    // Add response interceptor for better error handling
    this.client.interceptors.response.use(
      (response) => {
        // Detect if we were redirected
        if (response.request?.responseURL && this.credentials && !this.preserveBaseUrl) {
          const requestedUrl = this.client.defaults.baseURL;
          const finalUrl = new URL(response.request.responseURL).origin;

          if (requestedUrl !== finalUrl) {
            console.log(`Redirect detected: ${requestedUrl} -> ${finalUrl}`);
            this.actualServerUrl = finalUrl;
            if (!this.preserveBaseUrl) {
              this.client.defaults.baseURL = finalUrl;
            }
          }
        }
        return response;
      },
      (error) => {
        if (error.code === 'ECONNABORTED') {
          throw new Error('Request timeout - server took too long to respond');
        }
        if (error.response) {
          throw new Error(`Server error: ${error.response.status}`);
        }
        if (error.request) {
          throw new Error('Network error - unable to reach server. Check CORS or server URL.');
        }
        throw error;
      }
    );
  }

  /**
   * Detect if running in Electron
   */
  private isElectron(): boolean {
    // Check if running in Electron environment
    return !!(
      typeof window !== 'undefined' &&
      (window as any).electronAPI?.isElectron
    );
  }

  /**
   * Sleep helper for retry logic
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Generate cache key from parameters
   */
  private getCacheKey(params: Record<string, any>): string {
    return JSON.stringify(params);
  }

  /**
   * Get cached data if valid
   */
  private getFromCache<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    const now = Date.now();
    if (now - entry.timestamp > entry.ttl) {
      // Cache expired
      this.cache.delete(key);
      return null;
    }

    return entry.data as T;
  }

  /**
   * Store data in cache
   */
  private setInCache<T>(key: string, data: T, ttl?: number): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl: ttl || this.defaultCacheTTL,
    });
  }

  /**
   * Clear entire cache
   */
  clearCache(): void {
    this.cache.clear();
    console.log('API cache cleared');
  }

  /**
   * Clear cache for specific action
   */
  clearCacheForAction(action: string): void {
    for (const key of this.cache.keys()) {
      if (key.includes(action)) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { size: number; keys: string[] } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
    };
  }

  /**
   * Make a request with retry logic (using proxy in browser, direct in Electron)
   */
  private async makeRequest<T>(url: string, params?: any, retries = 3, timeoutMs?: number): Promise<T> {
    let lastError: Error | null = null;

    for (let attempt = 0; attempt < retries; attempt++) {
      try {
        if (this.useProxy) {
          // Browser mode - use proxy server
          const fullUrl = `${this.client.defaults.baseURL}${url}?${new URLSearchParams(params).toString()}`;

          const response = await axios.post(
            this.proxyUrl,
            { url: fullUrl, method: 'GET' },
            timeoutMs ? { timeout: timeoutMs } : undefined
          );

          // Check for redirect
          if (response.data.redirected && response.data.finalUrl) {
            const finalUrl = new URL(response.data.finalUrl).origin;
            console.log(`Proxy detected redirect: ${this.client.defaults.baseURL} -> ${finalUrl}`);
            this.actualServerUrl = finalUrl;
            if (!this.preserveBaseUrl) {
              this.client.defaults.baseURL = finalUrl;
            }
          }

          return response.data.data as T;
        } else {
          // Electron mode - direct request
          console.log(`Making direct request to: ${url} (attempt ${attempt + 1}/${retries})`);
          const response = await this.client.get<T>(url, { params });
          return response.data;
        }
      } catch (error: any) {
        lastError = error;
        console.error(`Request attempt ${attempt + 1}/${retries} failed:`, error.message);

        // Don't retry on authentication errors or client errors
        if (error.response?.status >= 400 && error.response?.status < 500) {
          throw error;
        }

        // If this wasn't the last attempt, wait before retrying
        if (attempt < retries - 1) {
          // Exponential backoff: 1s, 2s, 4s
          const delay = Math.pow(2, attempt) * 1000;
          console.log(`Waiting ${delay}ms before retry...`);
          await this.sleep(delay);
        }
      }
    }

    // All retries failed
    throw lastError || new Error('Request failed after all retries');
  }

  /**
   * Make a cached request
   */
  private async makeCachedRequest<T>(
    url: string,
    params: Record<string, any>,
    ttl?: number
  ): Promise<T> {
    const cacheKey = this.getCacheKey(params);

    // Check cache first
    const cached = this.getFromCache<T>(cacheKey);
    if (cached) {
      console.log(`Cache hit for: ${params.action || 'request'}`);
      return cached;
    }

    // Make request and cache result
    const data = await this.makeRequest<T>(url, params);
    this.setInCache(cacheKey, data, ttl);
    console.log(`Cached response for: ${params.action || 'request'}`);

    return data;
  }

  /**
   * Get the actual server URL (after redirects)
   */
  getActualServerUrl(): string | null {
    return this.actualServerUrl || this.baseServerUrl || null;
  }

  /**
   * Get the configured base server URL (before any redirect)
   */
  getBaseServerUrl(): string {
    return this.baseServerUrl;
  }

  /**
   * Override the provider/base server URL at runtime (admin panel).
   * Re-points the client, drops any detected redirect target, and clears the cache.
   * Callers should force a re-login afterwards since credentials are server-scoped.
   */
  setServerUrl(url: string): void {
    const normalized = this.normalizeServerUrl(url);
    // No-op when unchanged: re-applying (e.g. the App mount effect) must NOT drop
    // the discovered redirect origin (actualServerUrl) or the cache mid-session.
    if (normalized === this.baseServerUrl) return;
    this.baseServerUrl = normalized;
    this.actualServerUrl = null;
    // New provider → reset the EPG circuit breaker.
    this.epgFailures = 0;
    this.epgOpenUntil = 0;
    this.client.defaults.baseURL = normalized;
    if (this.credentials) {
      this.credentials = { ...this.credentials, serverUrl: normalized };
    }
    this.clearCache();
  }

  /**
   * Set credentials and authenticate
   */
  async setCredentials(credentials: XtreamCredentials): Promise<boolean> {
    // Enforce the locked server URL regardless of user input
    const baseUrl = this.baseServerUrl;
    this.credentials = {
      ...credentials,
      serverUrl: baseUrl,
    };

    this.client.defaults.baseURL = baseUrl;

    try {
      const authResponse = await this.authenticate();
      this.authInfo = authResponse;
      return authResponse.user_info.auth === 1;
    } catch (error) {
      console.error('Authentication failed:', error);
      throw error;
    }
  }

  /**
   * Normalize server URL
   */
  private normalizeServerUrl(url: string): string {
    let normalized = url.trim();

    // Remove all trailing slashes
    normalized = normalized.replace(/\/+$/, '');

    // Ensure protocol
    if (!normalized.startsWith('http://') && !normalized.startsWith('https://')) {
      normalized = `http://${normalized}`;
    }

    return normalized;
  }

  /**
   * Get authentication parameters
   */
  private getAuthParams(): { username: string; password: string } {
    if (!this.credentials) {
      throw new Error('Credentials not set. Call setCredentials first.');
    }
    return {
      username: this.credentials.username,
      password: this.credentials.password,
    };
  }

  /**
   * Authenticate user
   */
  async authenticate(): Promise<XtreamAuthResponse> {
    const params = this.getAuthParams();
    return this.makeRequest<XtreamAuthResponse>('/player_api.php', params);
  }

  /**
   * Get Live Stream Categories (cached for 10 minutes)
   */
  async getLiveCategories(): Promise<XtreamCategory[]> {
    const params = {
      ...this.getAuthParams(),
      action: 'get_live_categories',
    };
    return this.makeCachedRequest<XtreamCategory[]>('/player_api.php', params, 10 * 60 * 1000);
  }

  /**
   * Get Live Streams (cached for 5 minutes)
   */
  async getLiveStreams(categoryId?: string): Promise<XtreamLiveStream[]> {
    const params: any = {
      ...this.getAuthParams(),
      action: 'get_live_streams',
    };

    if (categoryId) {
      params.category_id = categoryId;
    }

    return this.makeCachedRequest<XtreamLiveStream[]>('/player_api.php', params, 5 * 60 * 1000);
  }

  /**
   * Get VOD Categories (cached for 10 minutes)
   */
  async getVodCategories(): Promise<XtreamCategory[]> {
    const params = {
      ...this.getAuthParams(),
      action: 'get_vod_categories',
    };
    return this.makeCachedRequest<XtreamCategory[]>('/player_api.php', params, 10 * 60 * 1000);
  }

  /**
   * Get VOD Streams (cached for 5 minutes)
   */
  async getVodStreams(categoryId?: string): Promise<XtreamVodStream[]> {
    const params: any = {
      ...this.getAuthParams(),
      action: 'get_vod_streams',
    };

    if (categoryId) {
      params.category_id = categoryId;
    }

    return this.makeCachedRequest<XtreamVodStream[]>('/player_api.php', params, 5 * 60 * 1000);
  }

  /**
   * Get VOD Info
   */
  async getVodInfo(vodId: number): Promise<XtreamVodInfo> {
    const params = {
      ...this.getAuthParams(),
      action: 'get_vod_info',
      vod_id: vodId,
    };
    return this.makeRequest<XtreamVodInfo>('/player_api.php', params);
  }

  /**
   * Get Series Categories (cached for 10 minutes)
   */
  async getSeriesCategories(): Promise<XtreamCategory[]> {
    const params = {
      ...this.getAuthParams(),
      action: 'get_series_categories',
    };
    return this.makeCachedRequest<XtreamCategory[]>('/player_api.php', params, 10 * 60 * 1000);
  }

  /**
   * Get Series List (cached for 5 minutes)
   */
  async getSeries(categoryId?: string): Promise<XtreamSeries[]> {
    const params: any = {
      ...this.getAuthParams(),
      action: 'get_series',
    };

    if (categoryId) {
      params.category_id = categoryId;
    }

    return this.makeCachedRequest<XtreamSeries[]>('/player_api.php', params, 5 * 60 * 1000);
  }

  /**
   * Get Series Info
   */
  async getSeriesInfo(seriesId: number): Promise<XtreamSeriesInfo> {
    const params = {
      ...this.getAuthParams(),
      action: 'get_series_info',
      series_id: seriesId,
    };
    return this.makeRequest<XtreamSeriesInfo>('/player_api.php', params);
  }

  /**
   * Get EPG for a channel
   */
  async getEPG(
    streamId: number,
    limit?: number,
    opts?: { retries?: number; timeoutMs?: number }
  ): Promise<XtreamEPG> {
    const params: any = {
      ...this.getAuthParams(),
      action: 'get_short_epg',
      stream_id: streamId,
    };

    if (limit) {
      params.limit = limit;
    }

    // Circuit open → fail instantly (no network) so we stop hammering a dead EPG endpoint.
    if (Date.now() < this.epgOpenUntil) {
      throw new Error('EPG temporarily disabled (provider EPG endpoint unresponsive)');
    }
    try {
      const res = await this.makeRequest<XtreamEPG>(
        '/player_api.php',
        params,
        opts?.retries ?? 3,
        opts?.timeoutMs
      );
      this.epgFailures = 0;
      return res;
    } catch (e) {
      if (++this.epgFailures >= 2) {
        this.epgOpenUntil = Date.now() + 5 * 60 * 1000; // 5-min cooldown
        this.epgFailures = 0;
        console.warn('EPG circuit opened: provider EPG endpoint unresponsive — pausing EPG for 5 min');
      }
      throw e;
    }
  }

  /**
   * Get Simple Data Table (all content at once)
   */
  async getSimpleDataTable(streamType: 'live' | 'vod' | 'series') {
    const params = {
      ...this.getAuthParams(),
      action: 'get_simple_data_table',
      stream_type: streamType,
    };
    return this.makeRequest('/player_api.php', params);
  }

  /**
   * Get the full EPG table for a single channel (all available programs, not just
   * the short window). Cached ~10 min. Titles/descriptions are base64-encoded.
   */
  async getChannelEpg(streamId: number): Promise<XtreamEPG> {
    const params = {
      ...this.getAuthParams(),
      action: 'get_simple_data_table',
      stream_id: streamId,
    };
    return this.makeCachedRequest<XtreamEPG>('/player_api.php', params, 10 * 60 * 1000);
  }

  /**
   * Wrap URL with proxy for browser mode
   */
  private wrapStreamUrl(directUrl: string): string {
    if (this.useProxy && this.proxyStreams) {
      // In browser mode, proxy the stream through our server
      return `${this.streamUrl}/${encodeURIComponent(directUrl)}`;
    }
    // In Electron mode, use direct URL
    return directUrl;
  }

  /**
   * Build Live Stream URL
   */
  buildLiveStreamUrl(streamId: number, extension: string = 'm3u8'): string {
    if (!this.credentials) {
      throw new Error('Credentials not set');
    }
    const baseUrl = (!this.preserveBaseUrl && this.actualServerUrl)
      ? this.actualServerUrl
      : this.normalizeServerUrl(this.credentials.serverUrl);
    const directUrl = `${baseUrl}/live/${this.credentials.username}/${this.credentials.password}/${streamId}.${extension}`;
    return this.wrapStreamUrl(directUrl);
  }

  /**
   * Build VOD Stream URL
   */
  buildVodStreamUrl(streamId: number, extension: string = 'mp4'): string {
    if (!this.credentials) {
      throw new Error('Credentials not set');
    }
    const baseUrl = (!this.preserveBaseUrl && this.actualServerUrl)
      ? this.actualServerUrl
      : this.normalizeServerUrl(this.credentials.serverUrl);
    const directUrl = `${baseUrl}/movie/${this.credentials.username}/${this.credentials.password}/${streamId}.${extension}`;
    return this.wrapStreamUrl(directUrl);
  }

  /**
   * Build Series Episode URL
   */
  buildSeriesStreamUrl(streamId: number, extension: string = 'mp4'): string {
    if (!this.credentials) {
      throw new Error('Credentials not set');
    }
    const baseUrl = (!this.preserveBaseUrl && this.actualServerUrl)
      ? this.actualServerUrl
      : this.normalizeServerUrl(this.credentials.serverUrl);
    const directUrl = `${baseUrl}/series/${this.credentials.username}/${this.credentials.password}/${streamId}.${extension}`;
    return this.wrapStreamUrl(directUrl);
  }

  /**
   * Build a catch-up (timeshift) URL from a program's unix start time + duration.
   * Xtream expects the start formatted as `Y-m-d:H-i` and the duration in minutes.
   *
   * The start is computed deterministically in UTC after applying `offsetMinutes`
   * (the same EPG offset used to align the guide), so the formatted wall-clock
   * matches what the user sees and does not depend on the browser's timezone.
   */
  buildCatchupUrl(
    streamId: number,
    durationMinutes: number,
    startUnixSeconds: number,
    offsetMinutes: number = 0,
    extension: string = 'ts'
  ): string {
    if (!this.credentials) {
      throw new Error('Credentials not set');
    }
    const baseUrl = (!this.preserveBaseUrl && this.actualServerUrl)
      ? this.actualServerUrl
      : this.normalizeServerUrl(this.credentials.serverUrl);
    const d = new Date((startUnixSeconds + offsetMinutes * 60) * 1000);
    const pad = (n: number) => String(n).padStart(2, '0');
    const start = `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}:${pad(d.getUTCHours())}-${pad(d.getUTCMinutes())}`;
    const directUrl = `${baseUrl}/timeshift/${this.credentials.username}/${this.credentials.password}/${durationMinutes}/${start}/${streamId}.${extension}`;
    return this.wrapStreamUrl(directUrl);
  }

  /**
   * Build Timeshift URL (catch-up TV)
   */
  buildTimeshiftUrl(
    streamId: number,
    duration: number,
    startTimestamp: number,
    extension: string = 'ts'
  ): string {
    if (!this.credentials) {
      throw new Error('Credentials not set');
    }
    const baseUrl = (!this.preserveBaseUrl && this.actualServerUrl)
      ? this.actualServerUrl
      : this.normalizeServerUrl(this.credentials.serverUrl);
    const directUrl = `${baseUrl}/timeshift/${this.credentials.username}/${this.credentials.password}/${duration}/${startTimestamp}/${streamId}.${extension}`;
    return this.wrapStreamUrl(directUrl);
  }

  /**
   * Get user info
   */
  getUserInfo(): XtreamAuthResponse | null {
    return this.authInfo;
  }

  /**
   * Check if authenticated
   */
  isAuthenticated(): boolean {
    return this.authInfo !== null && this.authInfo.user_info.auth === 1;
  }

  /**
   * Clear credentials and logout
   */
  logout(): void {
    this.credentials = null;
    this.authInfo = null;
  }
}

// Singleton instance
export const xtreamAPI = new XtreamAPI();




